/**
 * ============================================================================
 * PAYMENT HISTORY COMPONENT - 100% Ready (Cameroon Focus)
 * ============================================================================
 * 
 * Payment history list with filtering and pagination
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePayment } from '../../hooks/usePayment';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import StatusBadge from '../common/StatusBadge';
import Tabs from '../common/Tabs';

/**
 * Payment History Component
 * @returns {JSX.Element}
 */
const PaymentHistory = () => {
  const navigate = useNavigate();
  const { getPaymentHistory, loading } = usePayment();
  const { formatCurrency, formatDate, language } = useI18n();

  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    loadPayments();
  }, [filter, page]);

  const loadPayments = async () => {
    try {
      const response = await getPaymentHistory({
        page,
        limit: 20,
        status: filter !== 'all' ? filter : undefined,
      });
      
      const data = response?.data || response || [];
      setPayments(data);
      setHasMore(data.length === 20);
    } catch (error) {
      console.error('Load payment history error:', error);
    }
  };

  const getPaymentIcon = (method) => {
    const icons = {
      card: '💳',
      mtn_mobile_money: '📱',
      orange_money: '🍊',
      mpesa: '📱',
      bank_transfer: '🏦',
    };
    return icons[method] || '💰';
  };

  const getPaymentMethodName = (method) => {
    const names = {
      card: language === 'fr' ? 'Carte bancaire' : 'Card',
      mtn_mobile_money: 'MTN Mobile Money',
      orange_money: 'Orange Money',
      mpesa: 'M-Pesa',
      bank_transfer: language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
    };
    return names[method] || method;
  };

  const filterTabs = [
    { id: 'all', label: language === 'fr' ? 'Toutes' : 'All' },
    { id: 'completed', label: language === 'fr' ? 'Complétées' : 'Completed' },
    { id: 'pending', label: language === 'fr' ? 'En attente' : 'Pending' },
    { id: 'failed', label: language === 'fr' ? 'Échouées' : 'Failed' },
  ];

  const filteredPayments = payments.filter((payment) => {
    if (filter === 'all') return true;
    return payment.status === filter;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 animate-fadeInUp">
          <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
            {language === 'fr' ? 'Historique des paiements' : 'Payment History'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {language === 'fr' ? 'Consultez tous vos paiements' : 'View all your payments'}
          </p>
        </div>

        {/* Filter Tabs */}
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <Tabs
            tabs={filterTabs}
            activeTab={filter}
            onTabChange={setFilter}
            variant="pills"
          />
        </Card>

        {/* Payments List */}
        {loading ? (
          <Card variant="glass" className="p-12">
            <div className="flex items-center justify-center">
              <Spinner size="lg" />
            </div>
          </Card>
        ) : filteredPayments.length === 0 ? (
          <Card variant="glass" className="p-12">
            <EmptyState
              icon="💰"
              title={language === 'fr' ? 'Aucun paiement' : 'No payments'}
              description={
                language === 'fr'
                  ? 'Vous n\'avez pas encore effectué de paiements'
                  : "You haven't made any payments yet"
              }
            />
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredPayments.map((payment, index) => (
              <Card
                key={payment.id || index}
                variant="glass"
                hover
                clickable
                onClick={() => navigate(`/payments/${payment.id}`)}
                className="animate-fadeInUp"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-2xl shadow-lg">
                      {getPaymentIcon(payment.method)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {getPaymentMethodName(payment.method)}
                        </h3>
                        <StatusBadge
                          variant={
                            payment.status === 'completed'
                              ? 'success'
                              : payment.status === 'pending'
                              ? 'warning'
                              : 'error'
                          }
                          size="sm"
                        >
                          {payment.status}
                        </StatusBadge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {formatCurrency(payment.amount || 0, payment.currency || 'XAF')}
                        </span>
                        {payment.createdAt && (
                          <span>{formatDate(payment.createdAt)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <svg
                    className="w-6 h-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => setPage((prev) => prev + 1)}
              loading={loading}
            >
              {language === 'fr' ? 'Charger plus' : 'Load More'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
